import { createFeature, createReducer, on } from '@ngrx/store';
import * as _ from 'lodash';
import {
  CourseCurriculumSectionV2,
  PartialEntity,
  StreamVideoInfoV3,
  SubsectionDetailV3,
  SubsectionTypeV3,
  UploadStateV2,
} from '../../models';
import { CourseActionsV2 } from './course.actions';
import { CourseLoadErrorV2, CourseStateV2, initialCourseStateV2 } from './course.state';

// Helper to parse error and determine error type
const parseLoadError = (error: any): CourseLoadErrorV2 => {
  const statusCode = error?.status || error?.statusCode;

  // Check for invalid UUID error
  if (error?.error?.errors?.some((e: any) => e.detail?.includes('not a valid UUID'))) {
    return {
      type: 'invalid_uuid',
      message: $localize`The course ID format is invalid.`,
      statusCode,
    };
  }

  // Check status codes
  if (statusCode === 404) {
    return {
      type: 'not_found',
      message: $localize`This course could not be found. It may have been deleted or you may not have access.`,
      statusCode,
    };
  }

  if (statusCode === 401 || statusCode === 403) {
    return {
      type: 'unauthorized',
      message: $localize`You don't have permission to access this course.`,
      statusCode,
    };
  }

  if (statusCode >= 500) {
    return {
      type: 'server_error',
      message: $localize`A server error occurred. Please try again later.`,
      statusCode,
    };
  }

  return {
    type: 'unknown',
    message: $localize`An unexpected error occurred while loading the course.`,
    statusCode,
  };
};

// Video processing
let pendingUpdates: StreamVideoInfoV3[] = [];
let updateTimeout: any = null;
const processUpdates = (state: CourseStateV2, updates: StreamVideoInfoV3[]) => {
  const updatedSections = state.courseCurriculum.results.sections.map((section) => {
    return {
      ...section,
      subsections: section.subsections.map((section: PartialEntity<SubsectionDetailV3>) => {
        const update = updates.find((u) => u.lecture_id === section?.lecture?.id);
        return update
          ? { ...section.lecture, video_info: { ...section?.lecture?.video_info, ...update } }
          : section.lecture;
      }),
    };
  });

  const updatedState = {
    ...state,
    courseCurriculum: {
      ...state.courseCurriculum,
      results: {
        course_date_updated: state.courseCurriculum.results.course_date_updated,
        sections: updatedSections,
      },
    },
    isProcessingUpdates: false,
  };
  return updatedState as CourseStateV2;
};

export const courseDetailsV2Reducer = createReducer(
  initialCourseStateV2,

  // Loading state - set when starting to load course
  on(CourseActionsV2.loadInstructorCourseInfoV2, (state) => {
    return {
      ...state,
      isLoading: true,
      courseLoadError: null, // Clear any previous error
    };
  }),

  // Course details

  on(CourseActionsV2.createCourseSuccess, (state, data) => {
    const newState = {
      ...state,
      courseDetails: data.course,
      courseCurriculum: {
        count: 0,
        next: null,
        previous: null,
        results: {
          course_date_updated: '',
          sections: [],
        },
      },
    };
    return newState;
  }),

  on(CourseActionsV2.removeCourseDetails, CourseActionsV2.loadAdminCourseDetailsSuccess, (state) => {
    return {
      ...state,
      courseDetails: initialCourseStateV2.courseDetails,
    };
  }),
  on(CourseActionsV2.loadCourseDetailsSuccess, CourseActionsV2.loadAdminCourseDetailsSuccess, (state, data) => {
    return {
      ...state,
      courseDetails: data,
      courseLoadError: null, // Clear error on success
      isLoading: false,
    };
  }),

  // Handle course loading failures
  on(CourseActionsV2.loadCourseDetailsFail, (state, { error }) => {
    return {
      ...state,
      courseLoadError: parseLoadError(error),
      isLoading: false,
    };
  }),

  on(CourseActionsV2.upsertCourseSuccess, (state, { courseDetails }) => {
    return {
      ...state,
      courseDetails: {
        ...courseDetails,
      },
    };
  }),
  // SSE event - merge partial course details update
  on(CourseActionsV2.updateCourseDetailsFromSSE, (state, { courseDetails }) => {
    if (!state.courseDetails) return state;
    return {
      ...state,
      courseDetails: {
        ...state.courseDetails,
        ...courseDetails,
      },
    };
  }),
  on(CourseActionsV2.upsertCoursePricing, (state, { pricing, currency }) => {
    return {
      ...state,
      courseDetails: {
        ...state.courseDetails,
        pricing: pricing,
        currency: currency,
        currency_preview: currency,
      },
    };
  }),

  on(CourseActionsV2.upsertCourseDetailsVideoInfoV2, (state, { video_info }) => {
    const newState = {
      ...state,
      courseDetails: {
        ...state.courseDetails,
        video_info: video_info,
      },
    };
    return newState;
  }),

  // Course curriculum
  on(CourseActionsV2.loadCourseCurriculumSuccess, CourseActionsV2.loadAdminCourseCurriculumSuccess, (state, data) => {
    return {
      ...state,
      courseCurriculum: data,
    };
  }),

  on(CourseActionsV2.autoLoadCourseCurriculumSuccess, (state, data) => {
    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: [...state.courseCurriculum.results.sections, ...data.results.sections],
        },
      },
    };
  }),

  on(CourseActionsV2.upsertCourseCurriculum, (state, { results }) => {
    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: { course_date_updated: state.courseCurriculum.results.course_date_updated, sections: results },
      },
    };
  }),

  on(CourseActionsV2.upsertCourseSubsection, (state, { results, sectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        return {
          ...section,
          subsections: results,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: { course_date_updated: state.courseCurriculum.results.course_date_updated, sections: updatedSections },
      },
    };
  }),

  // Section
  on(CourseActionsV2.createSectionV2, (state, { section }) => {
    const sections = _.cloneDeep(state.courseCurriculum.results.sections);

    const newSection: CourseCurriculumSectionV2 = {
      id: section.id!,
      chain_id: section.chain_id!,
      title: section.title || 'Untitled Section',
      position: section.position || sections.length + 1,
      subsections: section.subsections || [],
      date_updated: section.date_updated || new Date().toISOString(),
      source: section.source,
    };

    if (section.position! === -1) {
      sections.push(newSection);
    } else {
      sections.splice(section.position!, 0, newSection);
    }

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: { course_date_updated: state.courseCurriculum.results.course_date_updated, sections: sections },
      },
    };
  }),

  on(CourseActionsV2.upsertSectionV2, (state, { section }) => {
    const sections = _.cloneDeep(state.courseCurriculum.results);

    const index: number = sections.sections.findIndex((s) => s.id === section.id);
    const updatedSection = sections.sections[index];
    const newSection: CourseCurriculumSectionV2 = {
      ...updatedSection,
      ...section,
    };
    let updatedSections = sections.sections.map((s, i) => (i === index ? newSection : s));
    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: { course_date_updated: state.courseCurriculum.results.course_date_updated, sections: updatedSections },
      },
    };
  }),

  on(CourseActionsV2.deleteSectionSuccessV2, (state, { sectionId }) => {
    const sections = _.cloneDeep(state.courseCurriculum.results);
    const updatedSections = sections.sections.filter((s) => s.id !== sectionId);
    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: { course_date_updated: state.courseCurriculum.results.course_date_updated, sections: updatedSections },
      },
    };
  }),

  on(CourseActionsV2.deleteSubsectionV2, (state, { sectionId, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.filter((s) => s.id !== subsectionId);

        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: { course_date_updated: state.courseCurriculum.results.course_date_updated, sections: updatedSections },
      },
    };
  }),

  on(CourseActionsV2.softDeleteSubsectionToggleV2, (state, { sectionId, isDelete, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.map((subsection) => {
          if (subsection.id === subsectionId) {
            const updatedSubsection = { ...subsection };

            if (subsection.quiz) {
              updatedSubsection.quiz = { ...subsection.quiz, is_deleted: isDelete };
            } else if (subsection.assignment) {
              updatedSubsection.assignment = { ...subsection.assignment, is_deleted: isDelete };
            } else if (subsection.lecture) {
              updatedSubsection.lecture = { ...subsection.lecture, is_deleted: isDelete };
            }

            return updatedSubsection;
          }
          return subsection;
        });

        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          ...state.courseCurriculum.results,
          sections: updatedSections,
        },
      },
    };
  }),

  // Lecture
  on(CourseActionsV2.createLectureV2, (state, { subsection, lecture }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === subsection.section) {
        const newSubsection: SubsectionDetailV3 = {
          id: subsection.id,
          type: subsection.type as SubsectionTypeV3,
          position: section.subsections.length + 1,
          quiz: undefined,
          lecture: {
            ...lecture,
            date_created: new Date().toISOString(),
            date_updated: new Date().toISOString(),
          },
          assignment: undefined,
          section: subsection.section,
          date_updated: new Date().toISOString(),
        };

        return {
          ...section,
          subsections: [...section.subsections, newSubsection as SubsectionDetailV3],
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        // results: updatedSections as CourseCurriculumSectionV2[],
        results: { course_date_updated: state.courseCurriculum.results.course_date_updated, sections: updatedSections },
      },
      selectedSubsectionId: subsection.id,
      selectedActivityId: lecture.id,
      // isActivityModalOpen: false,
    };
  }),

  on(CourseActionsV2.upsertLectureV2, CourseActionsV2.upsertLectureSuccessV2, (state, { lecture }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === lecture.section) {
        // Find the matching subsection to update the lecture
        const updatedSubsections = section.subsections.map((subsection) => {
          if (subsection.lecture?.id === lecture.id) {
            return {
              ...subsection,
              lecture: {
                ...subsection.lecture,
                ...lecture,
                video_info: lecture.video_info,
                date_updated: new Date().toISOString(),
              },
              date_updated: new Date().toISOString(),
            };
          }
          return subsection;
        });

        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        // results: updatedSections,
        results: { course_date_updated: state.courseCurriculum.results.course_date_updated, sections: updatedSections },
      },
    };
  }),

  // Update store with backend response after lecture creation
  // This ensures we have the real IDs and any server-generated data (chain_id, etc.)
  on(CourseActionsV2.createLectureSuccessV2, (state, { subsection, lecture }) => {
    if (!lecture || !subsection) return state;

    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === subsection.section) {
        const updatedSubsections = section.subsections.map((sub) => {
          // Find the optimistically created subsection by matching the optimistic ID
          // or by the lecture ID if subsection was created by backend
          if (sub.id === subsection.id || sub.lecture?.id === lecture.id) {
            return {
              ...sub,
              id: subsection.id, // Update with real subsection ID from backend
              lecture: {
                ...sub.lecture,
                ...lecture,
                resources: lecture.resources || [], // Ensure resources array exists
              },
              chain_id: lecture.chain_id || sub.chain_id, // Update chain_id from backend
            };
          }
          return sub;
        });

        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections,
        },
      },
    };
  }),

  // on(CourseActionsV2.upsertLectureResourceV2, (state, { lecture, lectureResources }) => {
  //   const updatedSections = state.courseCurriculum.results.sections.map((section) => {
  //     if (section.id === lecture.section) {
  //       // Find the matching subsection to update the lecture
  //       const updatedSubsections = section.subsections.map((subsection) => {
  //         if (subsection.lecture?.id === lecture.id) {
  //           return {
  //             ...subsection,
  //             lecture: {
  //               ...subsection.lecture,
  //               resources: [...lectureResources, ...(subsection.lecture.resources ?? [])],
  //               date_updated: new Date().toISOString(),
  //             },
  //             date_updated: new Date().toISOString(),
  //           };
  //         }
  //         return subsection;
  //       });

  //       return {
  //         ...section,
  //         subsections: updatedSubsections,
  //       };
  //     }
  //     return section;
  //   });

  //   return {
  //     ...state,
  //     courseCurriculum: {
  //       ...state.courseCurriculum,
  //       results: { course_date_updated: state.courseCurriculum.results.course_date_updated, sections: updatedSections },
  //     },
  //   };
  // }),

  on(CourseActionsV2.upsertLectureResourceSuccessV2, (state, { lecture, updatedLectureResources }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === lecture.section) {
        const updatedSubsections = section.subsections.map((subsection) => {
          if (subsection.lecture?.id === lecture.id) {
            const lectureState = {
              ...subsection,
              lecture: {
                ...subsection.lecture,
                // ...lecture,
                resources: updatedLectureResources,
                date_updated: new Date().toISOString(),
              },
              date_updated: new Date().toISOString(),
            };
            return lectureState;
          }
          return subsection;
        });

        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          ...state.courseCurriculum.results,
          sections: updatedSections,
        },
      },
    };
  }),

  // Handle SSE event - add resource without API call
  on(CourseActionsV2.addLectureResourceFromSSE, (state, { lecture, lectureResource }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      // Use section from SSE event if available, otherwise search by lecture ID
      const sectionMatches = lecture.section
        ? section.id === lecture.section
        : section.subsections.some(sub => sub.lecture?.id === lecture.id);

      if (!sectionMatches) {
        return section;
      }

      const updatedSubsections = section.subsections.map((subsection) => {
        if (subsection.lecture?.id === lecture.id) {
          // Check if resource already exists (prevent duplicates)
          const existingResources = subsection.lecture.resources || [];
          const resourceExists = existingResources.some(r => r.id === lectureResource.id);

          if (resourceExists) {
            return subsection;
          }

          return {
            ...subsection,
            lecture: {
              ...subsection.lecture,
              resources: [...existingResources, lectureResource],
              date_updated: new Date().toISOString(),
            },
            date_updated: new Date().toISOString(),
          };
        }
        return subsection;
      });

      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          ...state.courseCurriculum.results,
          sections: updatedSections,
        },
      },
    };
  }),

  on(CourseActionsV2.deleteLectureResourceV2, (state, { lectureResourceId, sectionId, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.map((subsection) => {
          if (subsection.id === subsectionId && subsection.type === 'lecture') {
            const filteredQuestions =
              subsection.lecture?.resources?.filter((resource) => resource.id !== lectureResourceId) || [];
            return {
              ...subsection,
              lecture: {
                ...subsection.lecture,
                resources: filteredQuestions,
              },
            };
          }
          return subsection;
        });
        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        // results: updatedSections as any,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as any,
        },
      },
    };
  }),

  // on(CourseActionsV2.upsertLectureVideoInfoV2, (state, { video_info }) => {
  //   // TODO: Take lecture id from provided 'video_info',
  //   // TODO: Find subsection based on the lecture_id which coming with 'video_info'
  //   // TODO: Update the video_info inside subsection > lecture > video_info
  //   // NOTE: Do this process asynchronous way because this action will be call lot of time with lot of subsection update every time

  //   // Video info:

  //   let newState = {
  //     ...state,
  //     // TODO: Set updated data in store
  //   };

  //   return newState;
  // }),

  on(CourseActionsV2.upsertLectureVideoInfoV2, (state, { video_info }) => {
    const { lecture_id } = video_info;
    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: state.courseCurriculum.results?.sections.map((section) => ({
            ...section,
            subsections: section.subsections?.map((subsection) => {
              if (subsection.type === 'lecture' && subsection.lecture?.id === lecture_id) {
                const newSubsection = {
                  ...subsection,
                  lecture: {
                    ...subsection.lecture,
                    type: 'video',
                    video_info: {
                      ...subsection.lecture?.video_info,
                      ...video_info, // Merge the updated video_info
                    },
                  },
                };
                return newSubsection;
              }

              return subsection;
            }),
          })),
        },
      },
    } as CourseStateV2;
  }),

  on(CourseActionsV2.selectLectureVideoInfoFromLibraryV2, (state, { video_info, lecture_id }) => {
    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,

          sections: state.courseCurriculum.results?.sections?.map((section) => ({
            ...section,
            subsections: section.subsections?.map((subsection) => {
              if (subsection.type === 'lecture' && subsection.lecture?.id === lecture_id) {
                const newSubsection = {
                  ...subsection,
                  lecture: {
                    ...subsection.lecture,
                    type: subsection.lecture.type,
                    video_info: {
                      ...subsection.lecture?.video_info,
                      ...video_info, // Merge the updated video_info
                    },
                  },
                };
                return newSubsection;
              }

              return subsection;
            }),
          })),
        },
      },
    } as CourseStateV2;
  }),

  // Quiz
  on(CourseActionsV2.createQuizV2, (state, { subsection, quiz }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === subsection.section) {
        const newSubsection: SubsectionDetailV3 = {
          id: subsection.id,
          chain_id: subsection.chain_id,
          type: subsection.type as SubsectionTypeV3,
          position: section.subsections.length + 1,
          lecture: undefined,
          quiz: {
            ...quiz,
            questions: [], // Mark as fully loaded to prevent unnecessary GET call
            date_created: new Date().toISOString(),
            date_updated: new Date().toISOString(),
          },
          assignment: undefined,
          section: subsection.section,
          date_updated: new Date().toISOString(),
        };

        return {
          ...section,
          subsections: [...section.subsections, newSubsection as SubsectionDetailV3],
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
      selectedSubsectionId: subsection.id,
      selectedActivityId: quiz.id,
    };
  }),

  // Update store with real quiz data from POST response (avoids extra GET call)
  on(CourseActionsV2.createQuizSuccessV2, (state, { subsection, quiz }) => {
    if (!quiz || !subsection) return state;

    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === quiz.section) {
        const updatedSubsections = section.subsections.map((sub) => {
          if (sub.quiz?.id === quiz.id) {
            return {
              ...sub,
              id: subsection.id,
              quiz: {
                ...quiz,
                questions: [], // Mark as fully loaded
              },
            };
          }
          return sub;
        });
        return { ...section, subsections: updatedSubsections };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          ...state.courseCurriculum.results,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
      selectedSubsectionId: subsection.id,
      selectedActivityId: quiz.id,
    };
  }),

  on(CourseActionsV2.upsertQuizV2, (state, { quiz }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === quiz.section) {
        const updatedSubsections = section.subsections.map((subsection) => {
          if (subsection.quiz?.id === quiz.id) {
            return {
              ...subsection,
              quiz: {
                ...subsection.quiz,
                ...quiz,
                date_updated: new Date().toISOString(),
              },
              date_updated: new Date().toISOString(),
            };
          }
          return subsection;
        });

        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.createQuizQuestionV2, (state, { quizQuestion, quizAnswers, sectionId, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.map((subsection) => {
          if (subsection.id === subsectionId && subsection.type === 'quiz') {
            const updatedQuiz = {
              ...subsection.quiz,
              id: subsection.quiz?.id || quizQuestion.quiz,
              questions: [
                ...(subsection.quiz?.questions || []),
                {
                  ...quizQuestion,
                  answers: quizAnswers,
                },
              ],
            };
            return {
              ...subsection,
              quiz: updatedQuiz,
            };
          }
          return subsection;
        });
        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.upsertQuizQuestionV2, (state, { quizQuestion, quizAnswers, sectionId, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.map((subsection) => {
          if (subsection.id === subsectionId && subsection.type === 'quiz') {
            const updatedQuestions =
              subsection.quiz?.questions?.map((question) =>
                question.id === quizQuestion.id
                  ? { ...{ ...question, ...quizQuestion }, answers: quizAnswers }
                  : question
              ) || [];
            return {
              ...subsection,
              quiz: {
                ...subsection.quiz,
                questions: updatedQuestions,
              },
            };
          }
          return subsection;
        });
        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    let stateData = {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
    return stateData;
  }),

  on(CourseActionsV2.upsertQuizQuestionListsV2, (state, { quizQuestion, sectionId, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.map((subsection) => {
          if (subsection.id === subsectionId && subsection.type === 'quiz') {
            return {
              ...subsection,
              quiz: {
                ...subsection.quiz,
                questions: quizQuestion,
              },
            };
          }
          return subsection;
        });
        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.upsertQuizSettingsV2, (state, { quizQuestion, sectionId, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.map((subsection) => {
          if (subsection.id === subsectionId && subsection.type === 'quiz') {
            const updatedQuestions =
              subsection.quiz?.questions?.map((question) =>
                question.id === quizQuestion.id ? { ...quizQuestion } : question
              ) || [];
            return {
              ...subsection,
              quiz: {
                ...subsection.quiz,
                questions: updatedQuestions,
              },
            };
          }
          return subsection;
        });
        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    let stateData = {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };

    return stateData;
  }),

  on(CourseActionsV2.deleteQuizQuestionV2, (state, { quizQuestionId, sectionId, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.map((subsection) => {
          if (subsection.id === subsectionId && subsection.type === 'quiz') {
            const filteredQuestions =
              subsection.quiz?.questions?.filter((question) => question.id !== quizQuestionId) || [];
            return {
              ...subsection,
              quiz: {
                ...subsection.quiz,
                questions: filteredQuestions,
              },
            };
          }
          return subsection;
        });
        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.deleteQuizAnswerV2, (state, { quizAnswerId, quizQuestionId, sectionId, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.map((subsection) => {
          if (subsection.id === subsectionId && subsection.type === 'quiz') {
            const updatedQuestions =
              subsection.quiz?.questions?.map((question) => {
                if (question.id === quizQuestionId) {
                  const filteredAnswers = question.answers.filter((answer) => answer.id !== quizAnswerId);
                  return {
                    ...question,
                    answers: filteredAnswers,
                  };
                }
                return question;
              }) || [];

            return {
              ...subsection,
              quiz: {
                ...subsection.quiz,
                questions: updatedQuestions,
              },
            };
          }
          return subsection;
        });

        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  // Assignments
  on(CourseActionsV2.createAssignmentV2, (state, { subsection, assignment }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === subsection.section) {
        const newSubsection: SubsectionDetailV3 = {
          id: subsection.id,
          type: subsection.type as SubsectionTypeV3,
          position: section.subsections.length + 1,
          quiz: undefined,
          lecture: undefined,
          assignment: {
            ...assignment,
            date_created: new Date().toISOString(),
            date_updated: new Date().toISOString(),
          },
          section: subsection.section,
          date_updated: new Date().toISOString(),
        };

        return {
          ...section,
          subsections: [...section.subsections, newSubsection as SubsectionDetailV3],
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
      selectedSubsectionId: subsection.id,
      selectedActivityId: assignment.id,
    };
  }),

  on(CourseActionsV2.upsertAssignmentV2, (state, { assignment, assignmentQuestions }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      const updatedSubsections = section.subsections.map((subsection) => {
        if (subsection.assignment?.id === assignment.id) {
          const updatedAssignment = {
            ...subsection.assignment,
            ...assignment,
            questions: assignmentQuestions.map((question) => ({
              ...question,
              date_updated: new Date().toISOString(),
            })),
            date_updated: new Date().toISOString(),
          };

          return {
            ...subsection,
            assignment: updatedAssignment,
            date_updated: new Date().toISOString(),
          };
        }
        return subsection;
      });

      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.deleteAssignmentQuestionV2, (state, { assignmentQuestionId, sectionId, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.map((subsection) => {
          if (subsection.id === subsectionId && subsection.type === 'assignment') {
            const filteredQuestions =
              subsection.assignment?.questions?.filter((q) => q.id !== assignmentQuestionId) || [];
            return {
              ...subsection,
              assignment: {
                ...subsection.assignment,
                questions: filteredQuestions,
              },
            };
          }
          return subsection;
        });

        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  // Unified assignment with nested questions
  on(CourseActionsV2.upsertAssignmentWithQuestionsSuccessV2, (state, { assignment, subsection }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      // If creating new assignment (subsection provided)
      if (subsection && section.id === subsection.section) {
        const existingSubsection = section.subsections.find(s => s.id === subsection.id);
        if (!existingSubsection) {
          // Create new subsection with assignment
          const newSubsection: SubsectionDetailV3 = {
            id: subsection.id,
            type: 'assignment' as SubsectionTypeV3,
            position: section.subsections.length + 1,
            quiz: undefined,
            lecture: undefined,
            assignment: assignment,
            section: subsection.section,
            date_updated: new Date().toISOString(),
          };
          return {
            ...section,
            subsections: [...section.subsections, newSubsection],
          };
        }
      }

      // Update existing assignment
      const updatedSubsections = section.subsections.map((sub) => {
        if (sub.assignment?.id === assignment.id) {
          return {
            ...sub,
            assignment: {
              ...assignment,
              date_updated: new Date().toISOString(),
            },
            date_updated: new Date().toISOString(),
          };
        }
        return sub;
      });

      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
      selectedSubsectionId: subsection?.id || state.selectedSubsectionId,
      selectedActivityId: assignment.id,
    };
  }),

  // Load assignment data (smart caching - from GET API)
  on(CourseActionsV2.loadAssignmentSuccessV2, (state, { assignment, sectionId, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.map((sub) => {
          if (sub.id === subsectionId) {
            return {
              ...sub,
              assignment: {
                ...assignment,
                date_updated: new Date().toISOString(),
              },
              date_updated: new Date().toISOString(),
            };
          }
          return sub;
        });

        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  // Load quiz data (smart caching - from GET API)
  on(CourseActionsV2.loadQuizSuccessV2, (state, { quiz, sectionId, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.map((sub) => {
          if (sub.id === subsectionId) {
            return {
              ...sub,
              quiz: {
                ...quiz,
                date_updated: new Date().toISOString(),
              },
              date_updated: new Date().toISOString(),
            };
          }
          return sub;
        });

        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  // Load lecture data (from GET API, for articles and videos when drawer opens)
  on(CourseActionsV2.loadLectureSuccessV2, (state, { lecture, sectionId, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.map((sub) => {
          if (sub.id === subsectionId) {
            return {
              ...sub,
              lecture: {
                ...lecture,
                date_updated: new Date().toISOString(),
              },
              date_updated: new Date().toISOString(),
            };
          }
          return sub;
        });

        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.deleteAssignmentV2, (state, { assignmentId, sectionId, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        return {
          ...section,
          subsections: section.subsections.filter(s => s.id !== subsectionId),
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  // SSE event handlers for real-time collaboration
  on(CourseActionsV2.addAssignmentFromSSE, (state, { assignment, subsection }) => {
    // Similar to create, but from SSE event
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === subsection.section) {
        // Check if subsection already exists
        const exists = section.subsections.some(s => s.id === subsection.id);
        if (!exists) {
          const newSubsection: SubsectionDetailV3 = {
            id: subsection.id,
            type: 'assignment' as SubsectionTypeV3,
            position: section.subsections.length + 1,
            quiz: undefined,
            lecture: undefined,
            assignment: assignment,
            section: subsection.section,
            date_updated: new Date().toISOString(),
          };
          return {
            ...section,
            subsections: [...section.subsections, newSubsection],
          };
        }
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.updateAssignmentFromSSE, (state, { assignment }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      const updatedSubsections = section.subsections.map((sub) => {
        if (sub.assignment?.id === assignment.id) {
          return {
            ...sub,
            assignment: {
              ...assignment,
              date_updated: new Date().toISOString(),
            },
            date_updated: new Date().toISOString(),
          };
        }
        return sub;
      });

      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.deleteAssignmentFromSSE, (state, { assignmentId, sectionId, subsectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        return {
          ...section,
          subsections: section.subsections.filter(s => s.id !== subsectionId),
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  // SSE event handlers for lecture real-time collaboration
  on(CourseActionsV2.addLectureFromSSE, (state, { lecture, subsection }) => {
    // Similar to create, but from SSE event
    const currentSections = state.courseCurriculum.results.sections;
    console.log('[Reducer] addLectureFromSSE:', {
      lectureId: lecture?.id,
      subsectionId: subsection?.id,
      targetSectionId: subsection?.section,
      currentSectionsCount: currentSections?.length,
      currentSectionIds: currentSections?.map(s => s.id),
    });

    // Guard: If no sections loaded yet, log warning and return unchanged state
    if (!currentSections || currentSections.length === 0) {
      console.warn('[Reducer] addLectureFromSSE: No sections in store yet, SSE event may be arriving before curriculum loaded');
      return state;
    }

    let lectureAdded = false;
    const updatedSections = currentSections.map((section) => {
      if (section.id === subsection.section) {
        // Check if subsection already exists
        const exists = section.subsections.some(s => s.id === subsection.id);
        if (!exists) {
          lectureAdded = true;
          const newSubsection: SubsectionDetailV3 = {
            id: subsection.id,
            type: 'lecture' as SubsectionTypeV3,
            position: subsection.position ?? section.subsections.length + 1,
            quiz: undefined,
            lecture: lecture,
            assignment: undefined,
            section: subsection.section,
            date_updated: new Date().toISOString(),
          };
          console.log('[Reducer] addLectureFromSSE: Adding lecture to section', section.id);
          return {
            ...section,
            subsections: [...section.subsections, newSubsection],
          };
        } else {
          console.log('[Reducer] addLectureFromSSE: Subsection already exists', subsection.id);
        }
      }
      return section;
    });

    if (!lectureAdded) {
      console.warn('[Reducer] addLectureFromSSE: Section not found for lecture', {
        targetSectionId: subsection?.section,
        availableSectionIds: currentSections.map(s => s.id),
      });
    }

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.updateLectureFromSSE, (state, { lecture }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      const updatedSubsections = section.subsections.map((sub) => {
        if (sub.lecture?.id === lecture.id) {
          return {
            ...sub,
            lecture: {
              ...sub.lecture,
              ...lecture,
              date_updated: new Date().toISOString(),
            },
            date_updated: new Date().toISOString(),
          };
        }
        return sub;
      });

      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.addAssignmentQuestionFromSSE, (state, { assignmentId, question }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      const updatedSubsections = section.subsections.map((sub) => {
        if (sub.assignment?.id === assignmentId) {
          const existingQuestions = sub.assignment.questions || [];
          const questionExists = existingQuestions.some(q => q.id === question.id);

          if (!questionExists) {
            return {
              ...sub,
              assignment: {
                ...sub.assignment,
                questions: [...existingQuestions, question],
                date_updated: new Date().toISOString(),
              },
              date_updated: new Date().toISOString(),
            };
          }
        }
        return sub;
      });

      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.updateAssignmentQuestionFromSSE, (state, { assignmentId, question }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      const updatedSubsections = section.subsections.map((sub) => {
        if (sub.assignment?.id === assignmentId && sub.assignment.questions) {
          const updatedQuestions = sub.assignment.questions.map(q =>
            q.id === question.id ? { ...question, date_updated: new Date().toISOString() } : q
          );

          return {
            ...sub,
            assignment: {
              ...sub.assignment,
              questions: updatedQuestions,
              date_updated: new Date().toISOString(),
            },
            date_updated: new Date().toISOString(),
          };
        }
        return sub;
      });

      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.deleteAssignmentQuestionFromSSE, (state, { assignmentId, questionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      const updatedSubsections = section.subsections.map((sub) => {
        if (sub.assignment?.id === assignmentId && sub.assignment.questions) {
          return {
            ...sub,
            assignment: {
              ...sub.assignment,
              questions: sub.assignment.questions.filter(q => q.id !== questionId),
              date_updated: new Date().toISOString(),
            },
            date_updated: new Date().toISOString(),
          };
        }
        return sub;
      });

      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  // SSE event handlers for quiz real-time collaboration
  on(CourseActionsV2.addQuizFromSSE, (state, { quiz, subsection }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === subsection.section) {
        // Check if subsection already exists
        const exists = section.subsections.some(s => s.id === subsection.id);
        if (!exists) {
          const newSubsection: SubsectionDetailV3 = {
            id: subsection.id,
            type: 'quiz' as SubsectionTypeV3,
            position: subsection.position ?? section.subsections.length + 1,
            quiz: quiz,
            lecture: undefined,
            assignment: undefined,
            section: subsection.section,
            date_updated: new Date().toISOString(),
          };
          return {
            ...section,
            subsections: [...section.subsections, newSubsection],
          };
        }
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.updateQuizFromSSE, (state, { quiz }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      const updatedSubsections = section.subsections.map((sub) => {
        if (sub.quiz?.id === quiz.id) {
          return {
            ...sub,
            quiz: {
              ...sub.quiz,
              ...quiz,
              date_updated: new Date().toISOString(),
            },
            date_updated: new Date().toISOString(),
          };
        }
        return sub;
      });

      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  // SSE event handlers for quiz question real-time collaboration
  on(CourseActionsV2.addQuizQuestionFromSSE, (state, { quizId, question }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      const updatedSubsections = section.subsections.map((sub) => {
        if (sub.quiz?.id === quizId) {
          const existingQuestions = sub.quiz.questions || [];
          const questionExists = existingQuestions.some(q => q.id === question.id);

          if (!questionExists) {
            return {
              ...sub,
              quiz: {
                ...sub.quiz,
                questions: [...existingQuestions, question],
                date_updated: new Date().toISOString(),
              },
              date_updated: new Date().toISOString(),
            };
          }
        }
        return sub;
      });

      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.updateQuizQuestionFromSSE, (state, { quizId, question }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      const updatedSubsections = section.subsections.map((sub) => {
        if (sub.quiz?.id === quizId && sub.quiz.questions) {
          const updatedQuestions = sub.quiz.questions.map(q =>
            q.id === question.id ? { ...q, ...question, date_updated: new Date().toISOString() } : q
          );

          return {
            ...sub,
            quiz: {
              ...sub.quiz,
              questions: updatedQuestions,
              date_updated: new Date().toISOString(),
            },
            date_updated: new Date().toISOString(),
          };
        }
        return sub;
      });

      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.deleteQuizQuestionFromSSE, (state, { quizId, questionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      const updatedSubsections = section.subsections.map((sub) => {
        if (sub.quiz?.id === quizId && sub.quiz.questions) {
          return {
            ...sub,
            quiz: {
              ...sub.quiz,
              questions: sub.quiz.questions.filter(q => q.id !== questionId),
              date_updated: new Date().toISOString(),
            },
            date_updated: new Date().toISOString(),
          };
        }
        return sub;
      });

      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  on(CourseActionsV2.reorderQuizQuestionsFromSSE, (state, { quizId, positions }) => {
    // Build a position map for quick lookup
    const positionMap = new Map(positions.map(p => [p.quiz_question, p.position]));

    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      const updatedSubsections = section.subsections.map((sub) => {
        if (sub.quiz?.id === quizId && sub.quiz.questions) {
          // Update positions and sort
          const updatedQuestions = sub.quiz.questions
            .map(q => ({
              ...q,
              position: positionMap.has(q.id) ? positionMap.get(q.id) : q.position,
            }))
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

          return {
            ...sub,
            quiz: {
              ...sub.quiz,
              questions: updatedQuestions,
              date_updated: new Date().toISOString(),
            },
            date_updated: new Date().toISOString(),
          };
        }
        return sub;
      });

      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections as CourseCurriculumSectionV2[],
        },
      },
    };
  }),

  // Section SSE event handlers for real-time collaboration
  on(CourseActionsV2.addSectionFromSSE, (state, { section }) => {
    const sections = state.courseCurriculum.results.sections;
    console.log('[Reducer] addSectionFromSSE:', {
      sectionId: section?.id,
      sectionTitle: section?.title,
      currentSectionsCount: sections?.length,
    });

    // Check if section already exists
    const exists = sections.some(s => s.id === section.id);
    if (exists) {
      console.log('[Reducer] addSectionFromSSE: Section already exists, skipping');
      return state;
    }

    const newSection: CourseCurriculumSectionV2 = {
      id: section.id,
      chain_id: section.chain_id,
      title: section.title || 'Untitled Section',
      position: section.position ?? sections.length + 1,
      subsections: section.subsections || [],
      date_updated: section.date_updated || new Date().toISOString(),
      source: section.source,
    };

    // Insert at correct position based on position field
    const updatedSections = [...sections];
    if (section.position !== undefined && section.position >= 0 && section.position < sections.length) {
      updatedSections.splice(section.position, 0, newSection);
    } else {
      updatedSections.push(newSection);
    }

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections,
        },
      },
    };
  }),

  on(CourseActionsV2.updateSectionFromSSE, (state, { section }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((s) => {
      if (s.id === section.id) {
        return {
          ...s,
          ...section,
          date_updated: new Date().toISOString(),
        };
      }
      return s;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections,
        },
      },
    };
  }),

  on(CourseActionsV2.deleteSectionFromSSE, (state, { sectionId }) => {
    const updatedSections = state.courseCurriculum.results.sections.filter(s => s.id !== sectionId);

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections,
        },
      },
    };
  }),

  on(CourseActionsV2.reorderSectionsFromSSE, (state, { positions }) => {
    // Build a position map for quick lookup
    const positionMap = new Map(positions.map(p => [p.section, p.position]));

    // Update positions and sort
    const updatedSections = state.courseCurriculum.results.sections
      .map(section => ({
        ...section,
        position: positionMap.has(section.id) ? positionMap.get(section.id)! : section.position,
      }))
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections,
        },
      },
    };
  }),

  on(CourseActionsV2.reorderSubsectionsFromSSE, (state, { sectionId, positions }) => {
    // Build a position map for quick lookup
    const positionMap = new Map(positions.map(p => [p.subsection, p.position]));

    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      if (section.id === sectionId) {
        // Update positions and sort
        const updatedSubsections = section.subsections
          .map(sub => ({
            ...sub,
            position: positionMap.has(sub.id) ? positionMap.get(sub.id)! : sub.position,
          }))
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

        return {
          ...section,
          subsections: updatedSubsections,
        };
      }
      return section;
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections,
        },
      },
    };
  }),

  on(CourseActionsV2.deleteLectureFromSSE, (state, { lectureId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      const updatedSubsections = section.subsections.filter(
        sub => sub.lecture?.id !== lectureId
      );
      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections,
        },
      },
    };
  }),

  on(CourseActionsV2.deleteLectureResourceFromSSE, (state, { lectureId, resourceId }) => {
    const updatedSections = state.courseCurriculum.results.sections.map((section) => {
      const updatedSubsections = section.subsections.map((sub) => {
        if (sub.lecture?.id === lectureId && sub.lecture.resources) {
          return {
            ...sub,
            lecture: {
              ...sub.lecture,
              resources: sub.lecture.resources.filter(r => r.id !== resourceId),
              date_updated: new Date().toISOString(),
            },
            date_updated: new Date().toISOString(),
          };
        }
        return sub;
      });
      return {
        ...section,
        subsections: updatedSubsections,
      };
    });

    return {
      ...state,
      courseCurriculum: {
        ...state.courseCurriculum,
        results: {
          course_date_updated: state.courseCurriculum.results.course_date_updated,
          sections: updatedSections,
        },
      },
    };
  }),

  // Lerner TODO: enable it if required
  on(CourseActionsV2.loadCourseLearnerSuccess, CourseActionsV2.loadAdminCourseLearnerSuccess, (state, data) => {
    return {
      ...state,
      courseLerner: data,
    };
  }),

  // Activity handler
  on(CourseActionsV2.createActivityV2, (state, { sectionId }) => {
    return {
      ...state,
      selectedSectionId: sectionId,
      selectedSubsectionId: '',
      selectedActivityId: '',
      activityState: undefined,
      isActivityModalOpen: true,
    };
  }),
  on(CourseActionsV2.editActivityV2, (state, { sectionId, subsectionId, activityId, activityState }) => {
    return {
      ...state,
      selectedSectionId: sectionId,
      selectedSubsectionId: subsectionId,
      selectedActivityId: activityId,
      activityState,
      isActivityModalOpen: true,
    };
  }),
  on(CourseActionsV2.closeActivityV2, (state) => {
    return {
      ...state,
      selectedSectionId: '',
      selectedSubsectionId: '',
      selectedActivityId: '',
      activityState: undefined,
      isActivityModalOpen: false,
    };
  }),
  on(CourseActionsV2.modalNextV2, (state, { activityState }) => {
    return {
      ...state,
      activityState,
    };
  }),
  on(CourseActionsV2.reloadModalV2, (state, { activityState }) => {
    return {
      ...state,
      activityState: activityState === 'type_lecture' ? undefined : activityState,
    };
  }),

  // File upload
  on(CourseActionsV2.uploadStartV2, (state, { referenceId, fileName, fileType, status }) => {
    return {
      ...state,
      uploads: {
        ...state.uploads,
        [referenceId]: {
          referenceId,
          fileName,
          fileType,
          status,
        } as UploadStateV2,
      },
    };
  }),
  on(CourseActionsV2.uploadProgressV2, (state, { referenceId, bytesTotal, bytesUploaded, progressPercent, status }) => {
    if (!state.uploads[referenceId]) {
      return state;
    }
    return {
      ...state,
      uploads: {
        ...state.uploads,
        [referenceId]: {
          ...state.uploads[referenceId],
          bytesTotal,
          bytesUploaded,
          progressPercent,
          status,
        } as UploadStateV2,
      },
    };
  }),
  on(CourseActionsV2.uploadStatusUpdateV2, (state, { referenceId, status, error }) => {
    const updateStatus = {
      ...state,
      uploads: {
        ...state.uploads,
        [referenceId]: {
          ...state.uploads[referenceId],
          status: status,
          error,
        } as UploadStateV2,
      },
    };
    return updateStatus;
  }),
  on(CourseActionsV2.uploadCompleteV2, CourseActionsV2.uploadAbortV2, (state, { referenceId }) => {
    return {
      ...state,
      uploads: _.omit(state.uploads, referenceId),
    };
  }),

  // Annotations
  on(CourseActionsV2.loadAnnotationsSuccessV2, (state, annotations) => {
    return {
      ...state,
      annotations: annotations,
    };
  }),

  // Admin
  on(CourseActionsV2.loadAdminCourseHistorySuccess, (state, coursesVersionHistory) => {
    return {
      ...state,
      coursesVersionHistory: coursesVersionHistory,
    };
  }),

  // Instructor course permissions
  on(CourseActionsV2.loadCoursePermissionsSuccess, (state, { coursePermissions }) => {
    return {
      ...state,
      coursePermissions: coursePermissions,
    };
  }),

  //livesession
  //load courses live session (replaces existing courses - initial load)
  on(CourseActionsV2.loadCoursesSuccess, (state, { courses, count, page }) => {
    return {
      ...state,
      courses: courses,
      coursesCount: count,
      coursesLoaded: true,
      coursesCurrentPage: page,
    };
  }),

  // Load more courses (append to existing)
  on(CourseActionsV2.loadMoreCoursesSuccess, (state, { courses, count, page }) => {
    // Filter out duplicates by id
    const existingIds = new Set(state.courses.map((c) => c.id));
    const newCourses = courses.filter((c) => !existingIds.has(c.id));
    return {
      ...state,
      courses: [...state.courses, ...newCourses],
      coursesCount: count,
      coursesCurrentPage: page,
    };
  }),

  // Reset courses cache
  on(CourseActionsV2.resetCoursesCache, (state) => {
    return {
      ...state,
      courses: [],
      coursesCount: 0,
      coursesLoaded: false,
      coursesCurrentPage: 1,
    };
  }),

  //load course live session
  on(CourseActionsV2.loadCourseSuccess, (state, { course }) => {
    return {
      ...state,
      course: course,
    };
  }),

  //class setup
  on(CourseActionsV2.loadClassSetupSuccess, (state, { data }) => {
    return {
      ...state,
      classSetup: {
        ...state.classSetup,
        duration: data.data.duration,
        max_participants: data.data.max_participants,
        minimum_booking_notice: data.data.minimum_booking_notice,
        minimum_booking_notice_unit: data.data.minimum_booking_notice_unit,
        before_event_buffer: data.data.before_event_buffer,
        after_event_buffer: data.data.after_event_buffer,
        time_slot_interval: data.data.time_slot_interval,
        show_session_details_conflict: data.data.show_session_details_conflict,
        schedule_id: data.data.schedule.id,
        schedule: data.data.schedule,
        // Session timing settings
        end_warning_minutes: data.data.end_warning_minutes,
        grace_period_minutes: data.data.grace_period_minutes,
        back_to_back_threshold_minutes: data.data.back_to_back_threshold_minutes,
        auto_kick_enabled: data.data.auto_kick_enabled,
        // Late enrollment settings
        grant_recording_access_to_late_enrollees: data.data.grant_recording_access_to_late_enrollees,
        // Webinar settings
        is_webinar: data.data.is_webinar ?? false,
        // Booking slug settings — `loadClassSetup` fires on fresh course
        // loads (including cross-course navigation), so we MUST NOT fall
        // back to the previous store value here; doing so would leak the
        // prior course's slug/hidden state into the new course's view if
        // the backend ever omitted these fields. The GET endpoint
        // (`get_course_event_type_details`) is guaranteed to include
        // both, so the hard `?? ''` / `?? false` defaults are correct.
        // The preserve-prior-state fallback only lives in
        // `upsertClassSetupSuccess` which is same-course by construction.
        custom_slug: data.data.custom_slug ?? '',
        is_booking_hidden: data.data.is_booking_hidden ?? false,
      },
    };
  }),

  on(CourseActionsV2.upsertClassSetupSuccess, (state, { data }) => {
    return {
      ...state,
      classSetup: {
        ...state.classSetup,
        duration: data.data.duration,
        max_participants: data.data.max_participants,
        minimum_booking_notice: data.data.minimum_booking_notice,
        minimum_booking_notice_unit: data.data.minimum_booking_notice_unit,
        before_event_buffer: data.data.before_event_buffer,
        after_event_buffer: data.data.after_event_buffer,
        time_slot_interval: data.data.time_slot_interval,
        show_session_details_conflict: data.data.show_session_details_conflict,
        schedule_id: data.data.schedule.id,
        schedule: data.data.schedule,
        // Session timing settings
        end_warning_minutes: data.data.end_warning_minutes,
        grace_period_minutes: data.data.grace_period_minutes,
        back_to_back_threshold_minutes: data.data.back_to_back_threshold_minutes,
        auto_kick_enabled: data.data.auto_kick_enabled,
        // Late enrollment settings
        grant_recording_access_to_late_enrollees: data.data.grant_recording_access_to_late_enrollees,
        // Webinar settings
        is_webinar: data.data.is_webinar ?? false,
        // Booking slug settings — Project-level fields, not on
        // LiveSessionBase. Older backend versions omit them from the
        // update response; preserve whatever the store already has in
        // that case so the hidden-toggle doesn't silently flip to false
        // after every save.
        custom_slug: data.data.custom_slug ?? state.classSetup?.custom_slug ?? '',
        is_booking_hidden:
          data.data.is_booking_hidden ?? state.classSetup?.is_booking_hidden ?? false,
      },
    };
  })
);

export const courseDetailsV2Feature = createFeature({
  name: 'courseStateV2',
  reducer: courseDetailsV2Reducer,
});
